import React, { useState, useEffect } from 'react';
import '../styles/Downloads.css';

/**
 * Página pública em /downloads — instaladores da versão desktop.
 *
 * Os links NÃO são fixos no código: vêm do mesmo manifest.json que alimenta o
 * auto-update (scripts/release-desktop.js o publica). Assim publicar uma release
 * nova atualiza esta página sozinha, sem alguém lembrar de editar a URL aqui.
 *
 * Se o manifesto não puder ser lido (release ainda não publicada, VPS fora do ar),
 * a página continua servindo: mostra os cartões desabilitados com um aviso, em
 * vez de links quebrados.
 */

const URL_MANIFESTO = '/releases/manifest.json';

/** Detecta a plataforma para destacar o download provável. */
const detectarSO = () => {
  const ua = `${navigator.userAgent} ${navigator.platform || ''}`.toLowerCase();
  if (ua.includes('win')) return 'windows';
  if (ua.includes('mac') || ua.includes('darwin')) return 'mac';
  return null;
};

const Downloads = () => {
  const [manifesto, setManifesto] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [so] = useState(detectarSO);

  useEffect(() => {
    let ativo = true;
    fetch(URL_MANIFESTO, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => { if (ativo) setManifesto(d); })
      .catch(() => { /* sem release publicada ainda — tratado no render */ })
      .finally(() => { if (ativo) setCarregando(false); });
    return () => { ativo = false; };
  }, []);

  const plataformas = [
    {
      id: 'windows',
      nome: 'Windows',
      icone: 'fab fa-windows',
      requisito: 'Windows 10 ou superior · 64 bits',
      url: manifesto?.installerURL || null,
    },
    {
      id: 'mac',
      nome: 'macOS',
      icone: 'fab fa-apple',
      requisito: 'macOS 11 ou superior · Intel e Apple Silicon',
      url: manifesto?.installerMacURL || null,
    },
  ];

  return (
    <div className="dl-pagina">
      <div className="dl-conteudo">
        <header className="dl-topo">
          <img src="/favicon.png" alt="seeLL" className="dl-logo" />
          <h1>Baixar o Consulta</h1>
          <p>
            O aplicativo para computador roda mais rápido que o navegador e se
            atualiza sozinho. Escolha a versão do seu sistema.
          </p>
          {manifesto?.version && (
            <div className="dl-versao">
              <i className="fas fa-circle" aria-hidden="true"></i>
              Versão {manifesto.version}
            </div>
          )}
        </header>

        <div className="dl-grade">
          {plataformas.map((p) => {
            const recomendado = so === p.id;
            const disponivel = Boolean(p.url) && !carregando;
            return (
              <div key={p.id} className={`dl-cartao${recomendado ? ' dl-recomendado' : ''}`}>
                {recomendado && <span className="dl-selo">Seu sistema</span>}
                <i className={`${p.icone} dl-icone`} aria-hidden="true"></i>
                <h2>{p.nome}</h2>
                <p className="dl-meta">{p.requisito}</p>
                {disponivel ? (
                  <a className="dl-botao" href={p.url} download>
                    <i className="fas fa-arrow-down" aria-hidden="true"></i>
                    Baixar
                  </a>
                ) : (
                  <span className="dl-botao dl-indisponivel">
                    {carregando ? 'Carregando...' : 'Indisponível'}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        <section className="dl-instrucoes">
          <h3>
            <i className="fas fa-list-check" aria-hidden="true"></i>
            Como instalar
          </h3>
          <ol className="dl-passos">
            <li>Baixe o arquivo <code>.zip</code> do seu sistema.</li>
            <li>
              Extraia <strong>os dois arquivos</strong> numa pasta fixa — por
              exemplo <code>C:\Callfarma\Consulta</code>. O programa precisa do
              arquivo <code>resources.neu</code> ao lado dele para funcionar.
            </li>
            <li>
              Abra o programa e, se quiser, crie um atalho na área de trabalho.
            </li>
            <li>
              Na primeira vez o sistema pode avisar que o programa é
              desconhecido. No Windows, clique em <strong>Mais informações →
              Executar assim mesmo</strong>. No Mac, clique com o botão direito
              no aplicativo e escolha <strong>Abrir</strong>.
            </li>
          </ol>
          <div className="dl-nota">
            <i className="fas fa-circle-info" aria-hidden="true"></i>
            <span>
              Escolha uma pasta onde você tenha permissão para gravar. Instalar
              dentro de <code>Arquivos de Programas</code> impede que o
              aplicativo se atualize sozinho.
            </span>
          </div>
        </section>

        <footer className="dl-rodape">
          <a href="/">
            <i className="fas fa-arrow-left" aria-hidden="true"></i> Usar pelo navegador
          </a>
          <p>Sistema de Gestão seeLL</p>
        </footer>
      </div>
    </div>
  );
};

export default Downloads;
